import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type CalendarEventType =
  | "milestone"
  | "wo_schedule"
  | "draw_deadline"
  | "inspection"
  | "permit"
  | "meeting"
  | "personal"
  | "system_alert"
  | "payment_due";

export type CalendarVisibility = "personal" | "team" | "project" | "company";

export interface CalendarEvent {
  id: string;
  company_id: string;
  project_id: string | null;
  title: string;
  description: string | null;
  event_type: CalendarEventType;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  visibility: CalendarVisibility;
  linked_wo_id: string | null;
  linked_draw_id: string | null;
  linked_invoice_id: string | null;
  is_auto_generated: boolean;
  auto_source: string | null;
  attendees: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  project_name?: string;
}

export const EVENT_TYPE_CONFIG: Record<
  CalendarEventType,
  { label: string; color: string; bgClass: string; textClass: string; borderClass: string }
> = {
  milestone: {
    label: "Milestone",
    color: "hsl(40, 100%, 50%)",
    bgClass: "bg-amber-500/15",
    textClass: "text-amber-600 dark:text-amber-400",
    borderClass: "border-l-amber-500",
  },
  wo_schedule: {
    label: "Work Order",
    color: "hsl(200, 100%, 50%)",
    bgClass: "bg-blue-500/15",
    textClass: "text-blue-600 dark:text-blue-400",
    borderClass: "border-l-blue-500",
  },
  draw_deadline: {
    label: "Draw / Billing",
    color: "hsl(145, 100%, 40%)",
    bgClass: "bg-emerald-500/15",
    textClass: "text-emerald-600 dark:text-emerald-400",
    borderClass: "border-l-emerald-500",
  },
  inspection: {
    label: "Inspection",
    color: "hsl(46, 100%, 49%)",
    bgClass: "bg-yellow-500/15",
    textClass: "text-yellow-700 dark:text-yellow-400",
    borderClass: "border-l-yellow-500",
  },
  permit: {
    label: "Permit",
    color: "hsl(46, 100%, 49%)",
    bgClass: "bg-yellow-500/15",
    textClass: "text-yellow-700 dark:text-yellow-400",
    borderClass: "border-l-yellow-500",
  },
  meeting: {
    label: "Meeting",
    color: "hsl(270, 100%, 70%)",
    bgClass: "bg-purple-500/15",
    textClass: "text-purple-600 dark:text-purple-400",
    borderClass: "border-l-purple-500",
  },
  personal: {
    label: "Personal",
    color: "hsl(0, 100%, 70%)",
    bgClass: "bg-red-400/15",
    textClass: "text-red-500 dark:text-red-400",
    borderClass: "border-l-red-400",
  },
  system_alert: {
    label: "System Alert",
    color: "hsl(0, 100%, 53%)",
    bgClass: "bg-red-600/15",
    textClass: "text-red-600 dark:text-red-400",
    borderClass: "border-l-red-600",
  },
  payment_due: {
    label: "Payment Due",
    color: "hsl(30, 100%, 50%)",
    bgClass: "bg-orange-500/15",
    textClass: "text-orange-600 dark:text-orange-400",
    borderClass: "border-l-orange-500",
  },
};

export const useCalendarEvents = (projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["calendar-events", projectId],
    queryFn: async () => {
      let q = (supabase.from("calendar_events") as any)
        .select("*, projects:project_id(name)")
        .order("start_date");

      if (projectId && projectId !== "all") {
        q = q.eq("project_id", projectId);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((e: any) => ({
        ...e,
        project_name: e.projects?.name || null,
      })) as CalendarEvent[];
    },
    enabled: !!user,
  });
};

export interface CreateCalendarEventInput {
  title: string;
  event_type: CalendarEventType;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  all_day?: boolean;
  visibility?: CalendarVisibility;
  description?: string;
  project_id?: string;
  linked_wo_id?: string;
  linked_draw_id?: string;
  linked_invoice_id?: string;
  attendees?: string[];
}

export const useCreateCalendarEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCalendarEventInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;

      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await (supabase.from("calendar_events") as any)
        .insert({
          company_id: companyId,
          title: input.title,
          event_type: input.event_type,
          start_date: input.start_date,
          end_date: input.end_date || input.start_date,
          start_time: input.start_time || null,
          end_time: input.end_time || null,
          all_day: input.all_day ?? true,
          visibility: input.visibility || "team",
          description: input.description || null,
          project_id: input.project_id || null,
          linked_wo_id: input.linked_wo_id || null,
          linked_draw_id: input.linked_draw_id || null,
          linked_invoice_id: input.linked_invoice_id || null,
          attendees: input.attendees || null,
          created_by: userData?.user?.id || null,
          is_auto_generated: false,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
    },
  });
};

export const useDeleteCalendarEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from("calendar_events") as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
    },
  });
};
